import api from './api';

/**
 * Fetches all orders for the current user.
 * @returns {Promise<Array>} A promise that resolves to an array of orders.
 */
export const getOrders = async (userId = null) => {
  try {
    const response = await api.get('/orders', { params: { userId } });
    return response.data;
  } catch (error) {
    console.error('Failed to fetch orders:', error);
    throw error;
  }
};

/**
 * Fetches a single order by ID
 * @param {number} orderId - The ID of the order
 * @returns {Promise<Object>} A promise that resolves to the order details
 */
export const getOrderById = async (orderId) => {
  try {
    const response = await api.get(`/orders/${orderId}/`);
    return response.data;
  } catch (error) {
    console.error('Failed to fetch order:', error);
    throw error;
  }
};

/**
 * Updates the status of an order
 * @param {string} orderId - The ID of the order to update
 * @param {Object} statusData - The new status data
 * @param {string} statusData.status - The new status (e.g., 'processing', 'shipped', 'delivered', 'cancelled')
 * @returns {Promise<Object>} A promise that resolves to the updated order
 */
export const updateOrderStatus = async (orderId, statusData) => {
  try {
    const response = await api.patch(`/orders/${orderId}/status`, statusData);
    return response.data;
  } catch (error) {
    console.error('Failed to update order status:', error);
    throw error;
  }
};

/**
 * Cancels an order
 * @param {number} orderId - The ID of the order to cancel
 * @returns {Promise<Object>} A promise that resolves to the cancelled order
 */
export const cancelOrder = async (orderId) => {
  try {
    const response = await api.post(`/orders/${orderId}/cancel/`);
    return response.data;
  } catch (error) {
    console.error('Failed to cancel order:', error);
    throw error;
  }
};

/**
 * Polling function for real-time order updates
 * @param {number} orderId - The ID of the order to track
 * @param {Function} callback - Callback function to call with updated order data
 * @param {number} interval - Polling interval in milliseconds (default: 10000)
 * @returns {Function} Cleanup function to stop polling
 */
export const startOrderPolling = (orderId, callback, interval = 10000) => {
  const pollOrder = async () => {
    try {
      const order = await getOrderById(orderId);
      callback(order);
    } catch (err) {
      console.error('Error polling order:', err);
    }
  };

  // Initial fetch
  pollOrder();

  // Set up polling interval
  const intervalId = setInterval(pollOrder, interval);

  // Return cleanup function
  return () => clearInterval(intervalId);
};

/**
 * Confirms order pickup with pickup code
 * @param {number} orderId - The ID of the order
 * @param {string} pickupCode - The pickup code
 * @returns {Promise<Object>} A promise that resolves to the confirmed order
 */
export const confirmOrderPickup = async (orderId, pickupCode) => {
  try {
    const response = await api.post(`/orders/${orderId}/confirm-pickup/`, { pickup_code: pickupCode });
    return response.data;
  } catch (error) {
    console.error('Failed to confirm pickup:', error);
    throw error;
  }
};
